import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { SparklesIcon } from '@heroicons/react/24/outline'
import { CustomSelect } from 'src/components/custom-select'
import { paymentAccountQueries } from 'src/services/payment-accounts/queries'
import { Statement } from 'src/services/statements/models'
import { SelectOption } from 'src/services/base-model'

interface AccountStepProps {
  statement: Statement
  selectedAccountId: string | null
  onChange: (accountId: string | null) => void
  isReadOnly?: boolean
}

/**
 * The Account wizard step: pick (or confirm the suggested) payment account.
 * The list comes from the payment-accounts endpoint, which only returns
 * accounts the user can access. It isn't narrowed to the billing org: the
 * account may belong to another org, and detection suggests from all of them.
 */
export const AccountStep = ({
  statement,
  selectedAccountId,
  onChange,
  isReadOnly = false,
}: AccountStepProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const { data: accounts, isLoading } = useQuery(
    paymentAccountQueries.list({ filters: { search } }),
  )

  const suggestedId = statement.suggestedPaymentAccount ?? null
  const hasSuggestion = Boolean(suggestedId) && !statement.paymentAccountConfirmed
  // The suggestion may fall outside the current search page; fetch it for its label.
  const { data: suggestedAccount } = useQuery(paymentAccountQueries.retrieve(suggestedId ?? ''))

  const options: SelectOption[] = useMemo(
    () => accounts?.results.map((a) => ({ label: a.name, value: a.id })) ?? [],
    [accounts?.results],
  )

  const selected: SelectOption[] = useMemo(() => {
    if (!selectedAccountId) return []
    const label =
      options.find((o) => o.value === selectedAccountId)?.label ??
      (statement.paymentAccountRef?.id === selectedAccountId
        ? statement.paymentAccountRef.name
        : undefined) ??
      (suggestedAccount?.id === selectedAccountId ? suggestedAccount.name : undefined)
    return [{ label: label ?? '', value: selectedAccountId }]
  }, [selectedAccountId, options, statement.paymentAccountRef, suggestedAccount])

  return (
    <div data-testid="account-step" className="max-w-xl space-y-3">
      <p className="text-sm text-text-secondary">{t('statementReview.accountStepHint')}</p>
      <CustomSelect
        options={options}
        values={selected}
        onChange={(values) => onChange(values[0]?.value ? String(values[0].value) : null)}
        placeholder={t('statementsPage.selectAccount')}
        searchable
        loading={isLoading}
        disabled={isReadOnly}
        searchFn={({ state }) => {
          setSearch(state.search)
          return options
        }}
        data-testid="account-step-select"
      />
      {hasSuggestion ? (
        <p
          className="flex items-start gap-1.5 text-sm text-text-secondary"
          data-testid="account-step-suggestion"
        >
          <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span>
            {t('statementReview.accountSuggested')}
            {statement.suggestedPaymentAccountReason
              ? ` — ${statement.suggestedPaymentAccountReason}`
              : ''}
          </span>
        </p>
      ) : (
        !statement.paymentAccount && (
          <p className="text-sm text-text-tertiary" data-testid="account-step-no-suggestion">
            {t('statementReview.accountNotDetected')}
          </p>
        )
      )}
    </div>
  )
}
